// navigation-completion Stage E — shared display helpers, styles and target
// draft conversion for the Inspector → Navigation tab. Pure, dependency-free and
// unit-testable; the JSX target editor lives in navTargetFields.tsx so this
// module exports no component (keeps fast-refresh happy).

import { type CSSProperties } from 'react';
import type {
  AuthorizationReason,
  DestinationPoint,
  EditTarget,
  InventoryDestination,
  LinkTarget,
  NavigationProposal,
  ProposalDisposition,
  ProposalOperation,
  RemoteWireTarget,
} from '@/api/documentNavigation';

// ---------------------------------------------------------------------------
// Styles (inline CSSProperties — project convention, matches LinksTab)
// ---------------------------------------------------------------------------

export const navStyles = {
  section: {
    padding: 12,
    border: '1px solid var(--border)',
    borderRadius: 6,
    background: 'var(--surface)',
    marginBottom: 12,
  } as CSSProperties,
  heading: { fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '0 0 8px 0' } as CSSProperties,
  muted: { fontSize: 12, color: 'var(--text-muted)' } as CSSProperties,
  label: { fontSize: 12, fontWeight: 600, color: 'var(--text)' } as CSSProperties,
  value: { fontSize: 12, color: 'var(--text)', wordBreak: 'break-all' } as CSSProperties,
  mono: { fontFamily: 'var(--font-mono, monospace)', fontSize: 11 } as CSSProperties,
  button: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  } as CSSProperties,
  buttonDisabled: {
    background: 'var(--surface2)',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
    borderRadius: 4,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'not-allowed',
  } as CSSProperties,
  smallBtn: {
    background: 'transparent',
    color: 'var(--accent, #4f46e5)',
    border: '1px solid var(--border)',
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 11,
    cursor: 'pointer',
  } as CSSProperties,
  smallBtnDisabled: {
    background: 'transparent',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 11,
    cursor: 'not-allowed',
  } as CSSProperties,
  input: {
    fontSize: 12,
    padding: '4px 6px',
    border: '1px solid var(--border)',
    borderRadius: 4,
    background: 'var(--surface)',
    color: 'var(--text)',
  } as CSSProperties,
  row: {
    padding: '8px 10px',
    border: '1px solid var(--border)',
    borderRadius: 4,
    background: 'var(--surface2)',
    marginBottom: 6,
    fontSize: 12,
  } as CSSProperties,
  error: { fontSize: 12, color: 'var(--danger, #b91c1c)' } as CSSProperties,
  note: {
    fontSize: 11,
    color: 'var(--text-muted)',
    padding: '6px 8px',
    background: 'var(--surface2)',
    borderRadius: 4,
    marginTop: 6,
  } as CSSProperties,

  // -------------------------------------------------------------------------
  // phase-compiler-tab-pins (navigation visual alignment) — ADDITIVE keys only.
  //
  // These were added to bring the bookmark/hyperlink workspace into line with
  // the rest of the app (the flat accent-underline tab strip of MiddlePaneTabs,
  // a readable secondary/destructive button hierarchy, and a grouped toolbar).
  // The pre-existing keys above are shared by the Validation/Release/Snapshot
  // surfaces this file also serves, so they are left UNCHANGED — only the
  // NavigationTab editing surface consumes the keys below.
  // -------------------------------------------------------------------------

  /** Flat tab strip matching the app's primary tabs (MiddlePaneTabs): a bottom
   *  rule with a 2px accent underline on the selected tab. */
  innerTabBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0,
    borderBottom: '1px solid var(--border)',
    marginBottom: 12,
  } as CSSProperties,
  innerTab: {
    padding: '8px 14px',
    background: 'transparent',
    border: 'none',
    // Longhand (not the `borderBottom` shorthand) so that toggling only the
    // colour on the active tab never mixes shorthand + longhand, which React
    // warns about during re-render.
    borderBottomWidth: 2,
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text-muted)',
    cursor: 'pointer',
  } as CSSProperties,
  innerTabActive: {
    color: 'var(--accent, #1B3A6B)',
    borderBottomColor: 'var(--accent, #1B3A6B)',
    fontWeight: 600,
  } as CSSProperties,

  /** Readable bordered secondary action — the default for inline row controls,
   *  sized for legibility rather than the tiny 11px native-looking buttons. */
  secondaryBtn: {
    background: 'var(--surface)',
    color: 'var(--text)',
    border: '1px solid var(--border)',
    borderRadius: 4,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  } as CSSProperties,
  secondaryBtnDisabled: {
    background: 'var(--surface2)',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
    borderRadius: 4,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'not-allowed',
  } as CSSProperties,

  /** Destructive action (Remove) — same geometry as the secondary button so the
   *  strip stays even, distinguished only by the danger colour. */
  dangerBtn: {
    background: 'var(--surface)',
    color: 'var(--danger, #b91c1c)',
    border: '1px solid var(--danger, #b91c1c)',
    borderRadius: 4,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  } as CSSProperties,
  dangerBtnDisabled: {
    background: 'var(--surface2)',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
    borderRadius: 4,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'not-allowed',
  } as CSSProperties,

  /** A grouped control bar (workspace actions, PDF toolbar). */
  toolbar: {
    display: 'flex',
    gap: 6,
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: 6,
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 6,
  } as CSSProperties,

  /** Lightweight container for an inner tab's editor panel — no heavy card
   *  border/background (the tab strip already frames it and the rows carry
   *  their own borders), which removes the double-boxed wasted chrome. */
  editorPanel: {
    paddingTop: 4,
  } as CSSProperties,
} as const;

// ---------------------------------------------------------------------------
// Pure formatters
// ---------------------------------------------------------------------------

export function describeDestination(d: InventoryDestination): string {
  switch (d.kind) {
    case 'localPage':
      return d.at
        ? `Page ${d.pageIndex + 1} at (${d.at.x.toFixed(0)}, ${d.at.y.toFixed(0)})`
        : `Page ${d.pageIndex + 1}`;
    case 'named':
      return `Named destination “${d.name}”`;
    case 'remote': {
      // Plain, uncluttered: the target DOCUMENT (its filename) and the page it
      // points to. The full relative path and the landing coordinates are
      // technical detail shown on demand in the row's disclosure, not here.
      const file = d.path.split('/').pop() || d.path;
      const where =
        typeof d.page === 'number'
          ? ` → page ${d.page + 1}`
          : d.name
            ? ` → “${d.name}”`
            : '';
      return `${file}${where}`;
    }
    case 'external':
      return `External link ${d.uri}`;
    case 'other':
      return `Other action (${d.detail})`;
    case 'none':
      return 'No destination';
    default: {
      const _never: never = d;
      return String(_never);
    }
  }
}

export function describeTarget(t: LinkTarget): string {
  switch (t.kind) {
    case 'localPage':
      return t.at
        ? `Page ${t.pageIndex + 1} at (${t.at.x.toFixed(0)}, ${t.at.y.toFixed(0)})`
        : `Page ${t.pageIndex + 1}`;
    case 'named':
      return `Named destination “${t.name}”`;
    case 'remoteGoToR':
      return t.dest
        ? `Remote ${t.path} → ${t.dest.kind === 'page' ? `page ${t.dest.pageIndex + 1}` : `“${t.dest.name}”`}`
        : `Remote ${t.path}`;
    default: {
      const _never: never = t;
      return String(_never);
    }
  }
}

export const OPERATION_LABEL: Readonly<Record<ProposalOperation, string>> = {
  createBookmark: 'Create bookmark',
  createHyperlink: 'Create hyperlink',
  linkExistingToc: 'Link existing contents entry',
};

export type DispositionTone = 'review' | 'accepted' | 'rejected' | 'conflict' | 'unknown';

export interface DispositionDisplay {
  readonly text: string;
  readonly tone: DispositionTone;
  readonly detail: string | null;
}

/** Map a server proposal disposition to a reviewer-facing label. Never claims a
 *  decision the server did not make; an auto-accept is surfaced as review
 *  because the server withholds automatic placement until qualified. */
export function describeDisposition(d: ProposalDisposition): DispositionDisplay {
  switch (d.kind) {
    case 'auto-accept':
      return { text: 'Needs review', tone: 'review', detail: 'awaiting reviewer acceptance' };
    case 'review':
      return { text: 'Needs review', tone: 'review', detail: d.reason };
    case 'unknown':
      return { text: 'Unknown', tone: 'unknown', detail: d.reason };
    case 'rejected':
      return { text: 'Rejected', tone: 'rejected', detail: null };
    case 'manual':
      return { text: 'Accepted (manual target)', tone: 'accepted', detail: describeTarget(d.target) };
    case 'conflict':
      return { text: 'Conflict — reassess', tone: 'conflict', detail: d.reason };
    default: {
      const _never: never = d;
      return { text: String(_never), tone: 'unknown', detail: null };
    }
  }
}

export function toneColor(tone: DispositionTone): string {
  switch (tone) {
    case 'accepted':
      return 'var(--ok, #15803d)';
    case 'rejected':
      return 'var(--text-muted)';
    case 'conflict':
      return 'var(--danger, #b91c1c)';
    case 'unknown':
      return 'var(--warning, #b45309)';
    case 'review':
    default:
      return 'var(--text)';
  }
}

export function authReasonText(reason: AuthorizationReason): string {
  switch (reason) {
    case 'permitted':
      return 'permitted by policy';
    case 'unknown-operation':
      return 'unknown operation';
    case 'policy-invalid':
      return 'the stored policy is invalid';
    case 'policy-not-activated':
      return 'the organisation navigation policy is not activated (validate-only)';
    case 'preserve-mode-forbids-transformation':
      return 'the preserve preset forbids any transformation';
    case 'signature-state-denies-mutation':
      return 'the artifact’s signature/protection state denies mutation';
    case 'operation-disabled-by-policy':
      return 'this operation is disabled by policy';
    case 'narrowed-by-user-preference':
      return 'a user preference narrowed this operation off';
    default: {
      const _never: never = reason;
      return String(_never);
    }
  }
}

/** The evidence summary shown under a proposal (precise: kind + page + text). */
export function describeEvidence(p: NavigationProposal): string {
  const e = p.evidence;
  const where = e.page > 0 ? `source page ${e.page}` : 'document level';
  return `${e.kind} · ${where} · “${e.text}”`;
}

/** A proposal whose resolved target cannot be trusted: the generator found no
 *  candidate (`candidateCount` 0) or produced an out-of-range placeholder (a
 *  local-page target with a negative index — which naïvely renders as "Page 0",
 *  or an empty named/remote destination). Such a proposal must NOT be accepted
 *  at its unverified target, nor offered a "jump to target page"; the reviewer
 *  can still accept it with an explicitly chosen manual target. */
export function isProposalTargetUnresolved(p: NavigationProposal): boolean {
  if (p.candidateCount <= 0) return true;
  const t = p.target;
  switch (t.kind) {
    case 'localPage':
      return !Number.isInteger(t.pageIndex) || t.pageIndex < 0;
    case 'named':
      return t.name.trim().length === 0;
    case 'remoteGoToR':
      return t.path.trim().length === 0;
    default: {
      const _never: never = t;
      return Boolean(_never);
    }
  }
}

// ---------------------------------------------------------------------------
// Target draft — local page, named destination, or a server-authorized remote
// (cross-document) selection.
//
// A remote target is NEVER a free-text path: the `remote` draft carries a
// `RemoteWireTarget` the picker (navTargetFields → RemoteTargetPicker) chose from
// the authoritative current/prior inventory, with its opaque id, context version
// and exact target hash. Accepting an arbitrary path here would be a target the
// backend cannot verify, so the picker is the only producer and an unfinished
// selection is rejected with a reason rather than guessed.
// ---------------------------------------------------------------------------

export type TargetDraft =
  | {
      readonly kind: 'localPage';
      readonly pageInput: string;
      /** A visually-selected /XYZ landing point (PDF user space), when the
       *  reviewer picked a destination area on the page rather than page-top. */
      readonly at?: DestinationPoint;
    }
  | { readonly kind: 'named'; readonly name: string }
  | { readonly kind: 'remote'; readonly selection: RemoteWireTarget | null };

export const EMPTY_LOCAL_TARGET: TargetDraft = { kind: 'localPage', pageInput: '1' };
export const EMPTY_REMOTE_TARGET: TargetDraft = { kind: 'remote', selection: null };

export function draftFromTarget(t: LinkTarget): TargetDraft {
  if (t.kind === 'localPage') {
    return t.at
      ? { kind: 'localPage', pageInput: String(t.pageIndex + 1), at: t.at }
      : { kind: 'localPage', pageInput: String(t.pageIndex + 1) };
  }
  if (t.kind === 'named') return { kind: 'named', name: t.name };
  // A resolved remoteGoToR cannot be re-expressed as a wire selection without the
  // inventory; start from an empty local draft (the reviewer re-picks a target).
  return EMPTY_LOCAL_TARGET;
}

/** Convert a UI draft to an EditTarget, validating FINITE numeric bounds for the
 *  in-document kinds. The server remains authoritative; this is a courtesy
 *  pre-check so an obviously invalid page never leaves the browser. A remote
 *  draft passes through the picker's verbatim server-authorized selection. */
export function targetFromDraft(
  draft: TargetDraft,
  pageCount: number | null,
): { readonly ok: true; readonly target: EditTarget } | { readonly ok: false; readonly error: string } {
  if (draft.kind === 'remote') {
    if (!draft.selection) {
      return { ok: false, error: 'choose a target document and a destination (page or named)' };
    }
    return { ok: true, target: draft.selection };
  }
  if (draft.kind === 'named') {
    const name = draft.name.trim();
    if (name.length === 0) return { ok: false, error: 'enter a named destination' };
    return { ok: true, target: { kind: 'named', name } };
  }
  const n = Number(draft.pageInput);
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return { ok: false, error: 'the page must be a whole number' };
  }
  if (n < 1) return { ok: false, error: 'the page must be 1 or greater' };
  if (pageCount !== null && n > pageCount) {
    return { ok: false, error: `the document has ${pageCount} page${pageCount === 1 ? '' : 's'}` };
  }
  if (draft.at) {
    if (!Number.isFinite(draft.at.x) || !Number.isFinite(draft.at.y)) {
      return { ok: false, error: 'the selected landing point is invalid' };
    }
    return { ok: true, target: { kind: 'localPage', pageIndex: n - 1, at: draft.at } };
  }
  return { ok: true, target: { kind: 'localPage', pageIndex: n - 1 } };
}
